import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Trading → Liquidate Account(s) → Liquidate Specific Account(s) / Liquidate All Client Account(s).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Liquidate Account(s) Details",
 * then "Liquidate Specific Account(s)" or "Liquidate All Client Account(s)".
 * Both sub-categories share the details screen; the second screen is a sleeve
 * table for Specific and a single confirmation for All. The sleeve table is
 * the household's full sleeve list (Get_Sleeve_Records, unfiltered). Shared
 * chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Liquidate Account(s)',
    subCategory: 'Liquidate Specific Account(s) or Liquidate All Client Account(s)',
    subject: subjectRow('Liquidate - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which sleeves you can pick, and for Liquidate All Client Account(s) it is the only thing that says whose accounts are liquidated. The advisor and office are taken from it.']],
    note: (
      <>
        <strong>Both sub-categories ask the same questions first.</strong> Only the last screen differs - a list
        of sleeves to tick for Specific, a single confirmation for All.
      </>
    ),
  }),
  {
    title: 'Answer the Liquidate Account(s) Details questions',
    body: (
      <p style={{ margin: 0 }}>
        Questions reveal themselves one at a time based on your answers, and every one is required once it
        appears. The last, <em>Please provide all relevant information for the Trading Team...</em>, becomes the
        case description the Trading team reads first.
      </p>
    ),
  },
  {
    title: 'Choose what is liquidated',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>Liquidate Specific Account(s):</strong> the screen warns <em>Please review carefully, this action
          can not be undone!</em> Tick every sleeve to liquidate in the <strong>Liquidate Specific
          Account(s)</strong> table (Account Number, Account Details, Model, Cash Balance, Balance, with a search
          bar). The rows you tick are recorded in the case details.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Liquidate All Client Account(s):</strong> there is no table. Confirm <em>Are you sure you wish to
          liquidate all of this Client&apos;s accounts? This action can not be undone.</em> by selecting{' '}
          <em>Yes (Liquidate all Accounts)</em>.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice - it governs timing and what the teams can and cannot do. Select{' '}
        <em>I have read, understand, and agree to the above information</em> under Acknowledgement and click Next.
        FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the details screen, then the sub-category screens.
const QUESTIONS = [
  {
    screen: 'Liquidate Account(s) Details',
    q: 'Are there Non-Qualified Accounts involved with this Request?',
    when: 'Always - this is the first question',
    options: ['Yes (There will be Non-Qualified Accounts that are liquidated)', 'No (There are no Non-Qualified accounts being liquidated)'],
  },
  {
    q: 'How would you like us to handle accounts that may have large capital gains? (Please note - we must have specific instructions otherwise the Case will be NIGOed. You may say "None" or "Nothing" though as well if there are no tax considerations)',
    when: 'Only if Non-Qualified accounts are involved',
    note: 'Give specific instructions - a vague answer is returned NIGO. "None" or "Nothing" is accepted when there are no tax considerations.',
    warn: true,
  },
  {
    q: 'Are the accounts being closed after liquidation?',
    when: 'If no Non-Qualified accounts are involved, or once the capital gains answer is filled in',
    options: ['Yes (The accounts are being closed after Liquidation)', 'No (The accounts are NOT being closed after Liquidation)'],
    note: 'Answering Yes skips the funds question and goes straight to the details box.',
  },
  {
    q: 'What would you like to do with the funds once they are raised?',
    when: 'Only if the accounts are NOT being closed',
    options: [
      'Move to a Distribution Sleeve (Leave there, we do NOT need Operations to Process a Distribution)',
      'Move to a Cash Only Sleeve (Leave as Cash)',
      'Move to a Cash Only Sleeve (Buy to Money Market first!)',
      "Leave it where it is (We'll let you know what to do with it when we are ready)",
      'Journal to another sleeve / Other',
    ],
  },
  {
    q: 'Please provide all relevant information for the Trading Team...',
    when: 'If the accounts are being closed, or once the funds question is answered',
    note: 'Becomes the case description.',
  },
  {
    screen: 'Liquidate Specific Account(s)',
    q: 'Liquidate Specific Account(s)',
    when: 'Liquidate Specific Account(s) only - always',
    note: 'Multi select - tick every sleeve to liquidate. Columns: Account Number, Account Details, Model, Cash Balance, Balance.',
    warn: true,
  },
  {
    screen: 'Liquidate All Client Account(s)',
    q: "Are you sure you wish to liquidate all of this Client's accounts? This action can not be undone.",
    when: 'Liquidate All Client Account(s) only - always',
    options: ['Yes (Liquidate all Accounts)'],
    warn: true,
    note: 'The only option. There is no account list - the household on the first screen decides whose accounts these are.',
  },
  {
    screen: 'Both sub-categories',
    q: 'Acknowledgement',
    when: 'Always, under the Operations and Trading notice on the last screen',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Liquidate Account(s)'),
  {
    h: 'The case came back NIGO over capital gains',
    p: [
      'When Non-Qualified accounts are involved, the form requires specific instructions for accounts that may have large capital gains. Say exactly what you want, or "None" / "Nothing" if there are no tax considerations.',
    ],
  },
  troubleSleeveRows('The Liquidate Specific Account(s) table is not filtered: every sleeve on the household is listed, highest cash balance first - inactive sleeves and Distribution, Contribution and cancelled (CNX) sleeves included. Check each account number before you tick it.'),
  {
    h: 'I picked the wrong household',
    p: [
      'The household chosen on the first screen decides which sleeves you can pick, and for Liquidate All Client Account(s) it is the only thing identifying whose accounts are liquidated. If it is wrong, cancel and start again rather than working around it.',
    ],
  },
];

export default function LiquidateAccounts() {
  return (
    <GuidePage
      title="Liquidate Account Requests"
      icon="cash-stack"
      path="Trading → Liquidate Account(s) → Liquidate Specific Account(s) or Liquidate All Client Account(s)"
      minutes={4}
      intro="How to ask the Prosperity Trading team to liquidate some or all of a client's accounts - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers.</Para>
        <Bullets items={[
          <><strong>Which sleeves are being liquidated</strong> - specific ones, or all of the household&rsquo;s accounts.</>,
          <><strong>Whether any are Non-Qualified</strong>, and if so <strong>specific instructions for large capital gains</strong>. Without them the case is returned NIGO.</>,
          <><strong>Whether the accounts are being closed</strong> after liquidation, and if not, <strong>what should happen to the cash</strong>.</>,
        ]} />
        <Callout tone="warn">
          The form warns twice that a liquidation can not be undone. Check the household and every account
          number before you submit.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not tax advice, and it does not describe how the
        Trading team handles the sales or the tax consequences for the client.
      </Footnote>
    </GuidePage>
  );
}
